






































  import { Vue, Component, Prop, Watch, Emit } from 'vue-property-decorator';
  import Debug from 'debug'
  import { v4 as uuidv4 } from 'uuid';
  import { chain, shuffle, Dictionary } from 'lodash';
  import { moveFocusToElement } from '@/app/accessibility/accessibility-utils';
  import { SerializedChoice } from '@xlearn/common';
  import * as Api from "@/app/question/components/api";
  import { ChoiceAnswer, ChoiceState } from './choice-interfaces';
  import HelpPopupComponent from "@/app/presenter/components/help-popup-component.vue";
  import { TranslateResult } from 'vue-i18n';
  import i18n from '@/plugins/i18n';

  const LONG_ANSWER_LENGTH: number = 100;

  const debug = Debug('choice-question-component')

  interface ChoiceItem {
    id: string;
    answer: string;
    score: number;
  }

@Component({
  components: {
    HelpPopupComponent,
  }
})
export default class ChoiceQuestionComponent extends Vue
 implements Api.QuestionComponent<SerializedChoice, ChoiceAnswer, ChoiceState> {
    @Prop({
      required: true,
    })
    public question: Api.Question<SerializedChoice>;

    @Prop({
      required: true,
    })
    public evaluated: boolean;

    @Prop({
      required: true,
    })
    public answer: Api.Answer<ChoiceAnswer> | null;

    @Prop({
      required: true,
    })
    public showSolution: boolean;

    @Prop({
      required: true,
    })
    public state: Api.State<ChoiceState> | null;

    private answers: ChoiceItem[] = [];

    private inputName: string = uuidv4();

    private selectedIndex: number = 0;

    private answersState: Dictionary<boolean> = {};

    private isThereLongAnswer: boolean = false;

    private get inputType(): string {
      return this.isMultiple ? 'checkbox' : 'radio';
    }

    private get isMultiple(): boolean {
      return this.correctAnswerIds.length > 1;
    }

    private get correctAnswerIds(): string[] {
      return this.answers
        .filter((answer: ChoiceItem) => answer.score > 0)
        .map((answer: ChoiceItem) => answer.id);
    }

    private get answersById(): Dictionary<ChoiceItem> {
      return chain(this.answers).keyBy('id').value();
    }

    private mounted(): void {
      debug('mounted');

      this.setDefaultAnswersState();
    }

    private getTabindex(index: number): 0|-1 {
      if (this.evaluated) {
        return -1;
      }

      if (this.selectedIndex === index) {
        return 0;
      }

      return -1;
    }

    private onSpaceKeyup(answerId: string): void {
      let checked: boolean = true;
      if (this.answersState[answerId]) {
        checked = false;
      }

      this.onAnswerChanged(checked, answerId);
    }

    private moveToAnswer(index: number, step: 1|-1): void {
      this.selectedIndex = moveFocusToElement(index, step, this.answers.length, (selectedIndex: number) => {
        return `#${this.getAnswerId(selectedIndex)}`;
      });
    }

    @Watch('answer')
    private answerChanged(newValue: Api.Answer<ChoiceAnswer> | null): void {
      if (!this.answer) {
        this.setDefaultAnswersState();
      }
    }

    private isLongAnswer(answer: string): boolean {
      const isLong: boolean = answer.length > LONG_ANSWER_LENGTH;
      if (isLong) {
        this.isThereLongAnswer = true;
      }
      return isLong || this.isThereLongAnswer;
    }

    private getAnswerClass(answerId: string): string {
      if (!this.showSolution) {
        return '';
      }

      if (this.answersById[answerId].score > 0) {
        return 'answer-correct';
      }

      return 'answer-incorrect';
    }

    private getAnswerId(index: number): string {
      return `${this.question.id}-step-${index}-tab`;
    }

    private onAnswerChanged(checked: boolean, answerId: string): void {
      debug('onAnswerChanged', checked, answerId)

      if (!this.isMultiple) {
        this.setAllToFalse();
      }

      this.answersState[answerId] = checked;
      const answerIds: string[] = this.getAllAnswerIds();
      const score: number = this.calculateAchievedScore();

      this.emitAnswerChanged({
        answer: {
          answerIds,
        },
        score,
      });
    }

    @Emit('answer-changed')
    public emitAnswerChanged(answer: Api.AnswerChangedPayload<ChoiceAnswer>) {
      return answer;
    }

    @Emit('state-changed')
    public emitStateChanged(state: Api.StateChangedPayload<ChoiceState>) {
      return state;
    }

    private calculateAchievedScore(): number {
      let score: number = 0;
      Object.entries(this.answersState).forEach(([answerId, checked]) => {
        if (checked) {
          const answer: ChoiceItem = this.answersById[answerId];
          score += answer.score as number;
        }
      });

      return score;
    }

    private isAnswerCorrect(answerId: string): boolean {
      const isAnswered = this.answersState[answerId];
      const isAnsweredAndCorrect: boolean = isAnswered && this.correctAnswerIds.includes(answerId);
      const isNotAnsweredAndIncorrect: boolean = !isAnswered && !this.correctAnswerIds.includes(answerId);

      return isAnsweredAndCorrect || isNotAnsweredAndIncorrect;
    }

    private setDefaultAnswersState(): void {
      this.answers = shuffle(this.question.content.answers);

      if (this.state !== null) {
        const order = this.state.state.answersOrder
        this.answers.sort((a, b) =>
              order.indexOf(a.id) - order.indexOf(b.id))
      }

      this.answersState = {};
      this.answer?.answer.answerIds.forEach(answerId => this.answersState[answerId] = true);

      this.selectedIndex = 0;

      if (!this.state) {
        this.emitStateChanged({
          state: {
            answersOrder: this.answers.map(answer => answer.id),
          }
        });
      }
    }

    private setAllToFalse(): void {
      for (const answerId of Object.keys(this.answersState)) {
        this.answersState[answerId] = false;
      }
    }

    private getAllAnswerIds(): string[] {
      let answerIds: string[] = [];
      Object.entries(this.answersState).forEach(([answerId, checked]) => {
        if (checked) {
          answerIds = [...answerIds, answerId];
        }
      });
      return answerIds;
    }

    private t(key: string, params: any = {}): TranslateResult {
      return i18n.t(key, params);
    }
  }
